/**
 * SS001-9 / SS001-11 — "you are here / ETA" for the Setup tab.
 *
 * The scorecard complaint was not that Setup lacked information: it showed
 * "3 / 13 steps complete" and a wall of rows. It was that nothing said which
 * step is live, whether the operator has to do anything, or how long the wait
 * is. This module answers those three questions from the data Setup already
 * polls, and nothing else.
 *
 * Pure — no Angular, no HTTP — so the wording and the arithmetic are unit
 * tested rather than eyeballed in a browser.
 */

export type SetupStageKey =
  | 'pick-domain'
  | 'check-sitemaps'
  | 'start-crawls'
  | 'crawling'
  | 'topic-prep'
  | 'finish-setup'
  | 'finishing'
  | 'ready';

export type CrawlPhase = 'idle' | 'crawling' | 'analyzing' | 'complete';

export interface PrepDomainProgress {
  domain: string;
  role: 'focal' | 'competitor';
  /** Sitemap has been checked, so the crawl can be scoped and started. */
  hasSitemapEstimate: boolean;
  phase: CrawlPhase;
}

export interface CompetitorPrepProgress {
  domain: string;
  /** Has analyzed pages. */
  crawled: boolean;
  hasTopicSignals: boolean;
  /** DomainTopicAggregate rows exist — this competitor can feed supporting pages. */
  ready: boolean;
  activeJob: 'queued' | 'running' | null;
  /** Crawled only head/error shells — will never produce topic signals. */
  contentBlocked: boolean;
}

export interface SetupGuideInput {
  hasDomain: boolean;
  prep: PrepDomainProgress[];
  competitors: CompetitorPrepProgress[];
  /** A focal pipeline step is queued or running. */
  pipelineBusy: boolean;
  /** Focal pipeline has produced everything Issues / Action Plan need. */
  reportReady: boolean;
  eta: CrawlEta | null;
}

export interface SetupGuide {
  stage: SetupStageKey;
  /** 0 while no domain is picked. */
  stepNumber: number;
  totalSteps: number;
  /** "Step 2 of 4 — Crawling & analyzing" */
  heading: string;
  /** One line of where-you-are detail. Never empty. */
  detail: string;
  /** "~14 min left", or null when not derivable yet. */
  eta: string | null;
  /**
   * What the operator must click now. `null` means the queue is handling it —
   * the whole point of SS001-9, so the UI leans on this to say "no action
   * needed" instead of leaving a dead button as the only hint.
   */
  action: string | null;
}

export const SETUP_TOTAL_STEPS = 4;

/** Live crawl ETA, aggregated across the prep set. */
export interface CrawlEta {
  seconds: number;
  /**
   * At least one domain is still fetching, so post-fetch analysis time is not
   * in this number yet. Reported as a floor, never as a promise.
   */
  lowerBound: boolean;
}

function pluralDomains(n: number): string {
  return n === 1 ? '1 domain' : `${n} domains`;
}

function andList(items: string[], max = 3): string {
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')} +${items.length - max} more`;
}

export function formatEta(eta: CrawlEta | null): string | null {
  if (!eta || !Number.isFinite(eta.seconds) || eta.seconds <= 0) return null;
  // Round only above a minute — 30s must not round up to "~1 min left".
  const mins = Math.max(1, Math.round(eta.seconds / 60));
  const text = eta.seconds < 60 ? 'under a minute left' : mins === 1 ? '~1 min left' : `~${mins} min left`;
  return eta.lowerBound ? `at least ${text}` : text;
}

/**
 * Which single step is live right now, and whether the operator owns it.
 *
 * Stage order mirrors the operator sequence in SETUP.md §12.5, collapsed to the
 * four things a person actually decides: scope, wait, (nothing — topic prep is
 * automatic), (nothing — the core pipeline is automatic too, SS001-11). Only
 * paid enrichments are still a decision, and they are not on this path.
 */
export function deriveSetupGuide(input: SetupGuideInput): SetupGuide {
  const totalSteps = SETUP_TOTAL_STEPS;

  if (!input.hasDomain) {
    return {
      stage: 'pick-domain',
      stepNumber: 0,
      totalSteps,
      heading: 'Pick a domain to set up',
      detail:
        'Enter the focal domain (and optional competitors) below to build a prep list, or choose an existing domain above.',
      eta: null,
      action: 'Build prep list or choose a domain'
    };
  }

  const prep = input.prep;
  const running = prep.filter((p) => p.phase === 'crawling' || p.phase === 'analyzing');
  const complete = prep.filter((p) => p.phase === 'complete');
  const notStarted = prep.filter((p) => p.phase === 'idle');

  // --- Step 1: scope, then start -----------------------------------------
  if (running.length === 0 && complete.length === 0) {
    const unchecked = notStarted.filter((p) => !p.hasSitemapEstimate);
    if (unchecked.length > 0) {
      return {
        stage: 'check-sitemaps',
        stepNumber: 1,
        totalSteps,
        heading: 'Step 1 of 4 — Check sitemaps',
        detail: `${pluralDomains(unchecked.length)} still need a sitemap check before you can scope and price the crawl: ${andList(
          unchecked.map((p) => p.domain)
        )}.`,
        eta: null,
        action: 'Check all sitemaps'
      };
    }
    return {
      stage: 'start-crawls',
      stepNumber: 1,
      totalSteps,
      heading: 'Step 1 of 4 — Start all crawls',
      detail:
        notStarted.length > 0
          ? `${pluralDomains(notStarted.length)} scoped and priced, none started yet.`
          : 'Add the competitors you want compared, then start the crawls.',
      eta: null,
      action: 'Start all crawls'
    };
  }

  // --- Step 2: the long wait ---------------------------------------------
  if (running.length > 0 || notStarted.length > 0) {
    const fetching = running.filter((p) => p.phase === 'crawling');
    const analyzing = running.filter((p) => p.phase === 'analyzing');
    const parts: string[] = [`${complete.length} of ${prep.length} domains done`];
    if (fetching.length > 0) parts.push(`crawling ${andList(fetching.map((p) => p.domain))}`);
    if (analyzing.length > 0) parts.push(`analyzing ${andList(analyzing.map((p) => p.domain))}`);
    if (notStarted.length > 0) parts.push(`${pluralDomains(notStarted.length)} not started`);

    return {
      stage: 'crawling',
      stepNumber: 2,
      totalSteps,
      heading: 'Step 2 of 4 — Crawling & analyzing',
      detail: `${parts.join(' · ')}. Topic prep starts on its own when the last one lands — you can leave this page.`,
      eta: formatEta(input.eta),
      // Not "wait": a not-started row is the operator's to start.
      action: notStarted.length > 0 ? 'Start the remaining crawls' : null
    };
  }

  // --- Step 3: topic prep, automatic (SS001-9) ---------------------------
  const inFlight = input.competitors.filter((c) => c.activeJob !== null);
  if (inFlight.length > 0) {
    return {
      stage: 'topic-prep',
      stepNumber: 3,
      totalSteps,
      heading: 'Step 3 of 4 — Topic prep running',
      detail: `Queued automatically when the crawls finished. ${pluralDomains(
        inFlight.length
      )} in progress: ${andList(inFlight.map((c) => c.domain))}. No action needed.`,
      eta: null,
      action: null
    };
  }

  const awaitingPrep = input.competitors.filter((c) => !c.ready && c.hasTopicSignals);
  if (awaitingPrep.length > 0) {
    // Crawls are done and these are preppable, but no job is queued yet — the
    // auto-enqueue is a beat behind the poll, or it was disabled/failed. Say so
    // and leave the manual button as the escape hatch rather than claiming
    // something is running.
    return {
      stage: 'topic-prep',
      stepNumber: 3,
      totalSteps,
      heading: 'Step 3 of 4 — Topic prep starting',
      detail: `${pluralDomains(awaitingPrep.length)} waiting on topic prep: ${andList(
        awaitingPrep.map((c) => c.domain)
      )}. This queues itself within a few seconds — if it does not, run it manually below.`,
      eta: null,
      action: null
    };
  }

  // --- Step 4: finish setup, automatic (SS001-11) ------------------------
  const blocked = input.competitors.filter((c) => !c.ready);
  const blockedNote = blocked.length
    ? ` ${andList(blocked.map((c) => c.domain))} produced no usable topic data and will be left out of the comparison.`
    : '';

  if (input.pipelineBusy) {
    return {
      stage: 'finishing',
      stepNumber: 4,
      totalSteps,
      heading: 'Step 4 of 4 — Finishing setup',
      detail: `Pipeline steps are running for this domain. No action needed.${blockedNote}`,
      eta: null,
      action: null
    };
  }

  if (input.reportReady) {
    return {
      stage: 'ready',
      stepNumber: 4,
      totalSteps,
      heading: 'Setup complete — open Issues',
      detail: `This domain has everything Issues and Action Plan need.${blockedNote}`,
      eta: null,
      action: null
    };
  }

  // Every domain is crawled and prepped, and the queue enqueues the core
  // pipeline itself (SS001-11). Same shape as the topic-prep hand-off above: say
  // it is coming, keep the manual button as the escape hatch, and never claim
  // something is already running when the gate is a poll behind.
  return {
    stage: 'finish-setup',
    stepNumber: 4,
    totalSteps,
    heading: 'Step 4 of 4 — Finishing setup',
    detail:
      'Every domain is crawled and topic-prepped. Links, topics, internal links and supporting pages queue themselves '
      + `within a few seconds — if they do not, use Finish setup below.${blockedNote}`,
    eta: null,
    action: null
  };
}

// --- ETA ------------------------------------------------------------------

interface EtaSample {
  at: number;
  done: number;
}

interface DomainEtaState {
  phase: CrawlPhase;
  total: number;
  samples: EtaSample[];
}

/** Poll observations needed before a rate is trusted (6s poll → ~18s). */
const MIN_SAMPLES = 3;
/** Rolling window; older samples are dropped so the rate tracks the live crawl. */
const MAX_SAMPLES = 12;
/** Below this the rate is noise, not signal. */
const MIN_WINDOW_MS = 10_000;

/**
 * Live ETA from observed progress, per domain.
 *
 * Crawl and analyze move at different rates, so each phase is timed on its own
 * and the sample window resets when the phase flips. There is no backend ETA to
 * borrow — the crawl-status endpoint has an `estimatedTimeRemaining` field that
 * nothing populates for domain crawls — and a made-up number is worse than
 * none, so this returns null until it has actually watched progress move.
 */
export class CrawlEtaTracker {
  private byDomain = new Map<string, DomainEtaState>();

  observe(
    domain: string,
    status: { phase?: CrawlPhase; totalUrls?: number; pagesCrawled?: number; pagesAnalyzed?: number } | null,
    nowMs: number = Date.now()
  ): void {
    if (!status) return;
    const phase = status.phase ?? 'idle';
    if (phase !== 'crawling' && phase !== 'analyzing') {
      this.byDomain.delete(domain);
      return;
    }

    const total = phase === 'crawling' ? status.totalUrls ?? 0 : status.pagesCrawled ?? status.totalUrls ?? 0;
    const done = phase === 'crawling' ? status.pagesCrawled ?? 0 : status.pagesAnalyzed ?? 0;

    const prev = this.byDomain.get(domain);
    // New phase, a restarted crawl, or a re-scoped total: the old rate no
    // longer describes this work, so start over rather than average across it.
    if (!prev || prev.phase !== phase || prev.total !== total || done < (prev.samples.at(-1)?.done ?? 0)) {
      this.byDomain.set(domain, { phase, total, samples: [{ at: nowMs, done }] });
      return;
    }

    prev.samples.push({ at: nowMs, done });
    if (prev.samples.length > MAX_SAMPLES) prev.samples.splice(0, prev.samples.length - MAX_SAMPLES);
  }

  forget(domain: string): void {
    this.byDomain.delete(domain);
  }

  reset(): void {
    this.byDomain.clear();
  }

  /** Seconds remaining in this domain's CURRENT phase, or null while sampling. */
  etaSecondsFor(domain: string): number | null {
    const state = this.byDomain.get(domain);
    if (!state || state.samples.length < MIN_SAMPLES || state.total <= 0) return null;

    const first = state.samples[0];
    const last = state.samples[state.samples.length - 1];
    const elapsedMs = last.at - first.at;
    const progressed = last.done - first.done;
    if (elapsedMs < MIN_WINDOW_MS || progressed <= 0) return null;

    const remaining = state.total - last.done;
    if (remaining <= 0) return 0;

    const perMs = progressed / elapsedMs;
    return Math.round(remaining / perMs / 1000);
  }

  /**
   * ETA for the whole prep set. Crawls run in parallel, so the set finishes when
   * the slowest one does. Flagged as a lower bound while any domain is still
   * fetching, because that domain's analyze pass has not been timed yet.
   */
  aggregate(domains: string[]): CrawlEta | null {
    let worst: number | null = null;
    let sawTracked = false;
    let anyFetching = false;

    for (const domain of domains) {
      const state = this.byDomain.get(domain);
      if (!state) continue;
      sawTracked = true;
      if (state.phase === 'crawling') anyFetching = true;
      const eta = this.etaSecondsFor(domain);
      if (eta === null) return null; // one unknown makes the aggregate unknown
      if (worst === null || eta > worst) worst = eta;
    }

    if (!sawTracked || worst === null) return null;
    return { seconds: worst, lowerBound: anyFetching };
  }
}
